import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSite } from "@/context/SiteContext";
import { UI } from "@/i18n";
import { fileUrl } from "@/lib/api";
import Reveal from "./Reveal";

const SPANS = [
  "md:col-span-2 md:row-span-2",
  "md:col-span-2 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-2",
  "md:col-span-2 md:row-span-1",
  "md:col-span-1 md:row-span-1",
  "md:col-span-1 md:row-span-1",
];
const FILTERS = ["all", "residential", "commercial", "custom"];

export default function Gallery() {
  const { content, t } = useSite();
  const [filter, setFilter] = useState("all");
  const g = content?.gallery_section;
  const items = (content?.gallery || []).filter((x) => filter === "all" || x.category === filter);

  return (
    <section id="work" className="py-24 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="stitch-x w-10 text-brand-red" />
              <span className="font-mono2 text-xs uppercase tracking-[0.3em] text-brand-red">{t(g?.eyebrow)}</span>
            </div>
            <h2 className="font-display uppercase text-4xl sm:text-5xl leading-[1.02]">{t(g?.title)}</h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`gallery-filter-${f}`}
                className={`px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition-all border ${
                  filter === f
                    ? "bg-brand-orange border-brand-orange text-brand-navy"
                    : "border-white/20 text-white/70 hover:border-brand-orange hover:text-brand-orange"
                }`}
              >
                {t(UI.galleryFilters[f])}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="grid grid-cols-2 md:grid-cols-4 auto-rows-[170px] sm:auto-rows-[210px] gap-3">
          <AnimatePresence mode="popLayout">
            {items.map((img, i) => (
              <motion.figure
                layout
                key={img.id}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45 }}
                data-testid={`gallery-item-${img.id}`}
                className={`bento-card relative overflow-hidden rounded-xl border border-white/10 group ${
                  i === 0 ? "col-span-2 row-span-2" : SPANS[(i + 1) % SPANS.length]
                }`}
              >
                <img src={fileUrl(img.url)} alt={t(img.caption)} loading="lazy" className="bento-img absolute inset-0 w-full h-full object-cover" />
                <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-[linear-gradient(0deg,rgba(11,19,43,0.92),transparent)] translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-400">
                  <span className="font-mono2 text-[10px] uppercase tracking-widest text-brand-orange">{img.category}</span>
                  <p className="text-sm font-semibold">{t(img.caption)}</p>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
